import { Component, OnInit, Output, Input, EventEmitter, ViewChild, SimpleChanges, ViewContainerRef, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Project } from '../../models/project';
import { ProjectTask } from '../../models/project-task';
import { CalendarFilterOptions, CalendarInitialView } from '../../models/calendar-fitler';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { UserCalendarService } from '../../services/user-calendar.service';
import { startOfDay, addHours, isAfter, isBefore, isEqual } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

@Component({
    selector: 'or-project-calendar',
    templateUrl: './project-calendar.component.html',
    styleUrls: ['./project-calendar.component.scss'],
    providers: [DatePipe, FullCalendarComponent],
    standalone: false
})
export class ProjectCalendarComponent implements OnInit {

  calendarOptions: CalendarOptions;
  showWeekends = true;
  isHidden = false;
  private _data: Array<Project>;
  private _filterOptions: CalendarFilterOptions;
  private _calendarApi: any;

  loading = true;
  @Input() dateField: string;
  @Input() startDateField: string;
  @Input() endDateField: string;
  @Input() titleField: string;
  @Input() subtitleField: string;
  @Input() profileField: string;
  @Input() footerField: string;
  @Input() idField: string;
  @Input() eventColorField: string;
  @Input() initialView: CalendarInitialView;
  @Input() initialDate: string;

  @Input() public set data(value: Array<Project>) {
    this._data = value;
    if (this._data)
      this.handleData(this._data);
    this.filterData(this._filterOptions);
  }
  get data(): any {
    return this._data;
  }
  @Input() public set filterOptions(value: CalendarFilterOptions) {
    this._filterOptions = value;
    if (this._filterOptions) {
      this.filterData(this._filterOptions);
    }
  }
  get filterOptions(): CalendarFilterOptions {
    return this._filterOptions;
  }
  @ViewChild('iconTemplate') iconTemplate!: TemplateRef<any>;
  @ViewChild('calendar') calendar: FullCalendarComponent;
  @Output() selected = new EventEmitter();
  @Output() selectedDoubleClick = new EventEmitter();
  @Output() selectedReoccurrence = new EventEmitter();
  @Output() calendarUnmounted = new EventEmitter();

  listenerFn: () => void;

  calendarPlugins = [dayGridPlugin];
  calendarEvents: EventInput[];

  oooDates: any[] = [];

  constructor(
    public datepipe: DatePipe,
    public userCalendarService: UserCalendarService,
    private viewContainerRef: ViewContainerRef,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.userCalendarService.GetInstanceCalendarDates().subscribe(oooDates => {
      this.oooDates = oooDates;
      this.loading = false;
      this.changeDetectorRef.detectChanges();
      if (this.calendar) {
        const calendarApi = this.calendar.getApi();
        if (calendarApi) {
          this._calendarApi = calendarApi;
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.calendar) {
      const calendarApi = this.calendar.getApi();
      if (calendarApi) {
        this._calendarApi = calendarApi;
      }
    }
  }

  ngOnDestroy() {
    if (this.calendar) {
      const currentCalendarDate = this._calendarApi.getDate().toISOString().slice(0, 10);
      this.calendarUnmounted.emit({
        date: currentCalendarDate,
        view: this._calendarApi.view.type,
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialDate'] && changes['initialDate'].currentValue) {
      const newDate = changes['initialDate'].currentValue;
      this.calendarOptions = {
        ...this.calendarOptions,
        initialDate: newDate,
      }
    }
    if (changes['initialView'] && changes['initialView'].currentValue) {
      const newView = changes['initialView'].currentValue;
      this.calendarOptions = {
        ...this.calendarOptions,
        initialView: newView
      }
    }
  }

  modifyTitle(eventIndex, newTitle) {
    this.calendarEvents[eventIndex].title = newTitle;
  }

  filterData(filterOptions: CalendarFilterOptions) {
    if (!this.calendarOptions || !filterOptions)
      return;
    const filteredEvents = this.calendarEvents?.map((event, index) => {
      if(index == 0) {
        // this is the default empty event
        return event;
      }
      let newEvent = event;
      const eventStart = filterOptions.filterViewBy == "project" ? event?.projectStartDate : event?.fieldStartDate;
      const eventEnd = filterOptions.filterViewBy == "project" ? event?.projectEndDate : event?.fieldEndDate;
      const leadingText = filterOptions.filterViewBy == "project" ? 'Project' : 'Field';

      if (filterOptions.filterDateBy === 'start') {
        if (!eventStart)
          return;
        newEvent.subtitle = `${leadingText} Start: ${this.datepipe.transform(new Date(eventStart), 'MM/dd/yy')}`;
        newEvent.isOneDay = true;
        newEvent.start = this.datepipe.transform(eventStart, 'yyyy-MM-dd');
        newEvent.end = this.datepipe.transform(eventStart, 'yyyy-MM-dd');
      } else if (filterOptions.filterDateBy === 'end') {
        if (!eventEnd)
          return;
        newEvent.subtitle = `${leadingText} End: ${this.datepipe.transform(new Date(eventEnd), 'MM/dd/yy')}`;
        newEvent.isOneDay = true;
        newEvent.start = this.datepipe.transform(eventEnd, 'yyyy-MM-dd');
        newEvent.end = this.datepipe.transform(eventEnd, 'yyyy-MM-dd');
      } else if (filterOptions.filterDateBy === 'startToEnd') {
        if (!eventStart || !eventEnd)
          return;
        newEvent.subtitle = `${leadingText} Dates: ${this.datepipe.transform(new Date(eventStart), 'MM/dd/yy')} - ${this.datepipe.transform(new Date(eventEnd), 'MM/dd/yy')}`;
        newEvent.isOneDay = this.datepipe.transform(new Date(eventStart), 'yyyy-MM-dd') === this.datepipe.transform(new Date(eventEnd), 'yyyy-MM-dd');
        newEvent.start = this.datepipe.transform(eventStart, 'yyyy-MM-dd');
        newEvent.end = this.datepipe.transform(eventEnd, 'yyyy-MM-dd');
      }
      return newEvent;
    }).filter(event => {
      return event;
    });
    this.calendarOptions.events = filteredEvents;
  }


  handleData(data: Array<Project>) {
    this.calendarEvents = [{ title: '', start: '', end: '', url: '', profile: '', footer: '', backgroundColor: '', isOneDay: false }];
    data.forEach(project => {
      if(project.projectStartDate == null || project.projectEndDate == null || project.fieldStartDate == null || project.fieldEndDate == null) {
        return;
      }

      let styles = this.getStyles(project);

      this.calendarEvents.push({
        start: null,
        end: null,
        subtitle: ``,
        isOneDay: true,
        title: (project["projectCode"] == null) ? '' : `${project["projectCode"]} - ${project["projectName"]}`,
        profile: "",
        profileTooltip: "",
        reoccurrenceTooltip: ``,
        eventid: project.id,
        stageColor: project[this.eventColorField],
        url: '',
        color: styles.card,
        textColor: styles.text,
        projectStartDate: project?.projectStartDate,
        projectEndDate: project?.projectEndDate,
        fieldStartDate: project?.fieldStartDate,
        fieldEndDate: project?.fieldEndDate,
      });

    });
    this.calendarOptions = {
      plugins: this.calendarPlugins,
      timeZone: 'UTC',
      initialView: this.initialView,
      initialDate: this.initialDate,
      headerToolbar: {
        left: '',
        center: 'prev title next',
        right: 'dayGridMonth,dayGridWeek,dayGridDay'
      },
      editable: true,
      events: this.calendarEvents,
      eventInteractive: false,
      buttonText: {
        dayGridMonth: 'Month',
        dayGridWeek: "Week",
        dayGridDay: "Day",
      },
      eventClick: (evt) => { this.eventClick(evt.event) },
      eventContent: (info) => { return this.eventRender(info) },

      dayCellDidMount: (info) => {
        if(info.view.type !== 'dayGridMonth'){
          return;
        }
        const oooForDay = this.oooDates.filter(d => {
          let start = startOfDay(new Date(d.startDate));
          let end = addHours(startOfDay(new Date(d.endDate)), 1);
          let day = startOfDay(info.date);
          return (isAfter(day, start) || isEqual(day, start)) && (isBefore(day, end) || isEqual(day, end));
        });
        if(oooForDay.length > 0){
          const container = this.viewContainerRef.createEmbeddedView(this.iconTemplate, {
            users: oooForDay,
            multiple: oooForDay.length > 1,
          });
          info.el.querySelector('.fc-daygrid-day-top')?.appendChild(container.rootNodes[0]);
        }
      }
    }
  }


  eventRender(info) {
    const template = `
    <div class="calendar-event ${info.event.extendedProps.isOneDay ? 'one-day' : ''} d-flex">
      <div class="info pr-3 ${info.event.extendedProps.isOneDay ? 'd-block' : 'd-flex'}">
        <span class="title d-block mb-0" style="${info.event.extendedProps.profile !== '' || info.event.extendedProps.reoccurrenceTooltip !== "" ? 'width: calc(100% - ' + (info.event.extendedProps.reoccurrenceTooltip !== "" && info.event.extendedProps.profile !== "" ? '35px)' : '13px)') : ''} ">
          ${info.event.title}
          <span class="subtitle ${info.event.extendedProps.isOneDay ? 'd-block' : 'd-inline ml-2'} mb-0">
            ${info.event.extendedProps.subtitle}
          </span>
        </span>
      </div>
      ${info.event.extendedProps.reoccurrenceTooltip !== "" ? `
        <div class="reoccurrence ${info.event.extendedProps.profile !== '' ? 'profileExists' : ''}">
          <i class="fak fa-repeat-rounded nav-font18"></i>
        </div>` : ""}
      ${info.event.extendedProps.profile !== "" ? `
      <div class="image-container circle medium">
        <div class="initials">
          <span>${info.event.extendedProps.profile}</span>
        </div>
      </div>` : ""}
    </div>`;

    let eventHtml = document.createElement('div')
    eventHtml.innerHTML = template;
    const reoccurrenceBtn = eventHtml.querySelector('.fa-repeat-rounded');
    if (reoccurrenceBtn) {
      reoccurrenceBtn.addEventListener('click', (e) => {
        this.eventReoccurrenceClick(info.event);
      });
    }

    let arrayOfDomNodes = [eventHtml]
    return { domNodes: arrayOfDomNodes }
  }

  addOpacityToHex(color, opacity) {
    var _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
    return color + _opacity.toString(16).toUpperCase();
  }

  getInitials(text) {
    if (text == null || text == undefined || text == "") return ""
    return text.split(' ').map(x => x.charAt(0)).join('').substr(0, 2).toUpperCase();
  }

  eventClick(event) {
    this.selected.emit(event.extendedProps);
  }

  eventDoubleClick(event) {
    this.selectedDoubleClick.emit(event.extendedProps);
  }

  eventReoccurrenceClick(event) {
    this.selectedReoccurrence.emit(event.extendedProps);
  }

  getStyles(stageItem: Project) {
    let cardColor = stageItem.currentStageColor;
    let textColor = "#FFF";
    if (cardColor == "#ED7892" || cardColor == "#F9D277" || cardColor == "#54B390") {
      textColor = "#333"
    }
    return { card: cardColor, text: textColor }
  }

  generateTooltip(info) {
    const assigneeDiv = info.el.querySelector('.image-container');
    var reoccurrenceDiv = info.el.querySelector('.reoccurrence');

    if (assigneeDiv) {
      const tooltip = document.createElement('div');
      var text = info.event.extendedProps.profileTooltip;
      tooltip.className = 'in show tooltip bs-tooltip-top tooltip-top top';
      tooltip.innerHTML = `
        <div class="tooltip-arrow arrow" style="left:30px"></div>
        <div class="tooltip-inner">${text}</div>`;
      const length = text.length;

      assigneeDiv.addEventListener('mouseenter', () => {
        document.body.appendChild(tooltip);
        const rect = assigneeDiv.getBoundingClientRect();
        tooltip.style.left = `${rect.left + window.pageXOffset - 45}px`;
        tooltip.style.top = `${rect.bottom + window.pageYOffset - (length < 25 ? 70 : (length < 35 ? 100 : 150))}px`;
      });
      assigneeDiv.addEventListener('mouseleave', () => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
    }
    if (reoccurrenceDiv) {
      const tooltip = document.createElement('div');
      var text = info.event.extendedProps.reoccurrenceTooltip;
      tooltip.className = 'in show tooltip bs-tooltip-bottom tooltip-bottom bottom reoccurrence-tooltip';
      tooltip.innerHTML = `
        <div class="tooltip-arrow arrow" style="left:30px"></div>
        <div class="tooltip-inner">${text}</div>`;

      reoccurrenceDiv.addEventListener('mouseenter', () => {
        document.body.appendChild(tooltip);
        const rect = reoccurrenceDiv.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        tooltip.style.left = `${rect.left + window.pageXOffset - (tooltipRect.width - 15)}px`;
        tooltip.style.top = `${rect.bottom + window.pageYOffset}px`;
      });
      reoccurrenceDiv.addEventListener('mouseleave', () => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
    }
  }
}
